import React, { useState, useEffect, useMemo } from 'react';

const PaymentCalculator = ({ vehiclePrice, selectedVehiclePrice, onClose }) => {
    // Tab and form state
    const [activeTab, setActiveTab] = useState('finance');
    const [price, setPrice] = useState(selectedVehiclePrice ? parseFloat(selectedVehiclePrice.replace(/[^0-9.-]+/g, '')) : 25000);
    const [taxRate, setTaxRate] = useState(9.25); // 9.25%
    const [fees, setFees] = useState(599); // Tag, title, dealer fees
    const [downPayment, setDownPayment] = useState(2000);
    const [term, setTerm] = useState(60); // months
    const [creditTier, setCreditTier] = useState('good');
    
    // Calculated values
    const [taxAmount, setTaxAmount] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [financedAmount, setFinancedAmount] = useState(0);
    const [monthlyPayment, setMonthlyPayment] = useState(0);
    
    // Credit tier interest rates (placeholders)
    const interestRates = useMemo(() => ({
        poor: 18.99,
        fair: 14.99,
        good: 9.99,
        great: 6.49
    }), []);
    
    // Calculate all values whenever inputs change
    useEffect(() => {
        // Calculate tax amount
        const calculatedTaxAmount = (price * (taxRate / 100));
        setTaxAmount(calculatedTaxAmount);
        
        // Calculate total price
        const calculatedTotalPrice = price + calculatedTaxAmount + fees;
        setTotalPrice(calculatedTotalPrice);
        
        // Calculate financed amount
        const calculatedFinancedAmount = calculatedTotalPrice - downPayment;
        setFinancedAmount(calculatedFinancedAmount);
        
        // Calculate monthly payment
        const interestRate = interestRates[creditTier] / 100 / 12; // monthly rate
        const numberOfPayments = term;
        
        // Use formula: M = P[r(1+r)^n]/[(1+r)^n-1]
        // Where M is monthly payment, P is principal, r is monthly rate, n is number of payments
        if (interestRate === 0) {
            setMonthlyPayment(calculatedFinancedAmount / numberOfPayments);
        } else {
            const monthlyPayment = calculatedFinancedAmount * 
                (interestRate * Math.pow(1 + interestRate, numberOfPayments)) / 
                (Math.pow(1 + interestRate, numberOfPayments) - 1);
            setMonthlyPayment(monthlyPayment);
        }
    }, [price, taxRate, fees, downPayment, term, creditTier, interestRates]);
    
    // Format as currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };
    
    return (
        <div className="bg-white p-6 max-w-2xl mx-auto relative rounded-lg shadow-lg">
            {/* Close button */}
            <button 
                onClick={() => onClose(false)}
                className="absolute -top-4 -right-4 text-gray-400 hover:text-white transition-colors bg-gray-700 rounded-full p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close dialog"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
            
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Payment Calculator</h2>
            
            {/* Tab switcher */}
            <div className="flex border-b border-gray-200 mb-6">
                <button
                    className={`px-4 py-2 text-sm font-medium ${
                        activeTab === 'finance'
                            ? 'text-red-600 border-b-2 border-red-600'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('finance')}
                >
                    Finance
                </button>
                <button
                    className={`px-4 py-2 text-sm font-medium ${
                        activeTab === 'cash'
                            ? 'text-red-600 border-b-2 border-red-600'
                            : 'text-gray-500 hover:text-gray-700'
                    }`}
                    onClick={() => setActiveTab('cash')}
                >
                    Pay Cash
                </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    {/* Input fields */}
                    <div className="my-8 mb-4 flex justify-between text-left">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Vehicle Price
                        </label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                            <input
                                type="number"
                                value={price}
                                onChange={(e) => setPrice(Math.max(0, parseFloat(e.target.value) || 0))}
                                className="block float-end w-full max-w-32 text-right pl-7 pr-3 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    
                    <div className="mb-4 hidden">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Sales Tax Rate (%)
                        </label>
                        <input
                            type="number"
                            value={taxRate}
                            onChange={(e) => setTaxRate(Math.max(0, parseFloat(e.target.value) || 0))}
                            className="block w-full px-3 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            step="0.1"
                        />
                    </div>
                    
                    <div className="mb-4 hidden">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tag/Title/Dealer Fees
                        </label>
                        <div className="relative">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                            <input
                                type="number"
                                value={fees}
                                onChange={(e) => setFees(Math.max(0, parseFloat(e.target.value) || 0))}
                                className="block w-full pl-7 pr-3 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    
                    {activeTab === 'finance' && (
                        <>
                            <div className="mb-4 flex justify-between text-left">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Down Payment
                                </label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">$</span>
                                    <input
                                        type="number"
                                        value={downPayment}
                                        onChange={(e) => setDownPayment(Math.max(0, parseFloat(e.target.value) || 0))}
                                        className="block text-right float-end w-full max-w-32 pl-7 pr-3 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            </div>
                            
                            <div className="mb-4 flex justify-between text-left">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Term (months)
                                </label>
                                <select
                                    value={term}
                                    onChange={(e) => setTerm(parseInt(e.target.value))}
                                    className="block w-full max-w-32 px-3 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value={24}>24 months</option>
                                    <option value={36}>36 months</option>
                                    <option value={48}>48 months</option>
                                    <option value={60}>60 months</option>
                                </select>
                            </div>
                            
                            <div className="mb-4 flex justify-between text-left">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Credit Tier
                                </label>
                                <select
                                    value={creditTier}
                                    onChange={(e) => setCreditTier(e.target.value)}
                                    className="block w-full max-w-32 px-3 py-2 rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="poor">Poor</option>
                                    <option value="fair">Fair</option>
                                    <option value="good">Good</option>
                                    <option value="great">Great</option>
                                </select>
                            </div>
                        </>
                    )}
                </div>

                
                
                <div className="bg-gray-50 p-4 rounded-lg">

                    {/* Result */}
                    {activeTab === 'finance' ? (
                        <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                            <div className="text-center">
                                <div className="text-lg text-blue-600 font-medium">Estimated Monthly Payment</div>
                                <div className="text-4xl font-bold text-blue-700 mt-1">
                                    {formatCurrency(monthlyPayment)}
                                </div>
                                {/*<div className="text-xs text-blue-500 mt-1">
                                    Total cost: {formatCurrency(monthlyPayment * term + downPayment)}
                                </div>*/}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-green-50 p-4 rounded-md border border-green-100">
                            <div className="text-center">
                                <div className="text-sm text-green-600 font-medium">Cash Payment</div>
                                <div className="text-3xl font-bold text-green-700 mt-1">
                                    {formatCurrency(totalPrice)}
                                </div>
                                {/*<div className="text-xs text-green-500 mt-1">
                                    Save {formatCurrency((monthlyPayment * term + downPayment) - totalPrice)} by paying cash!
                                </div>*/}
                            </div>
                        </div>
                    )}
                    
                    
                    {/* Price Breakdown */}
                    <div className="my-4 space-y-2 ">
                    <div className="border-t border-gray-300 pt-2 flex justify-between font-medium">
                    <span>Payment Details:</span>
                    </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Vehicle Price:</span>
                            <span className="font-medium">{formatCurrency(price)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Sales Tax ({taxRate}%):</span>
                            <span className="font-medium">{formatCurrency(taxAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Tag/Title/Fees:</span>
                            <span className="font-medium">{formatCurrency(fees)}</span>
                        </div>
                        <div className="border-t border-gray-300 pt-2 flex justify-between font-medium">
                            <span>Total Price:</span>
                            <span>{formatCurrency(totalPrice)}</span>
                        </div>
                        
                        {activeTab === 'finance' && (
                            <>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Down Payment:</span>
                                    <span className="font-medium">{formatCurrency(downPayment)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Amount Financed:</span>
                                    <span className="font-medium">{formatCurrency(financedAmount)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Est. Interest Rate:</span>
                                    <span className="font-medium">{interestRates[creditTier]}%</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Term:</span>
                                    <span className="font-medium">{term} months</span>
                                </div>
                            </>
                        )}
                    </div>
                    
                    
                    
                    <div className="text-xs text-gray-500 mt-4 text-left">
                        <strong>Disclaimer:</strong> Payment amount is an estimate for illustrative purposes only. This is not an advertisement or offer of credit. The estimated monthly payment may vary based on your credit profile and lender terms. The interest rate used in this calculation is an estimate and may not reflect the actual rate you receive. Actual rates may vary based on your creditworthiness and other factors.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PaymentCalculator;